import {
  humanizeFeatureCode,
  isModuleRootKind,
} from "@/v2-common/featureSurface/featureSurface.constants"
import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import { FEATURE_GROUP_RULE_KIND } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.constants"
import {
  isModuleRule,
  isPlaceholderName,
  pluralize,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import type { FeatureSurfaceCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/__types__/license.featureGroup.surfaceCatalog.query"
import { describeCheckedBy } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.checkedBy.utils"
import {
  describePackagesFact,
  describeWhoGetsIt,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.placement.utils"

export type LicenseFeatureGroupCapability =
  FeatureSurfaceCatalogQuery["getFeatureSurfaceCatalog"][number]

/** How a package reaches one capability: by its module's rule, or by hand. */
export type LicenseFeatureGroupCapabilityGrant = "module" | "pick"

export function hasModuleRule(
  rules: readonly FeatureGroupRuleLike[],
  moduleCode: string,
) {
  return rules.some((rule) => isModuleRule(rule, moduleCode))
}

/**
 * Why the package grants a capability, or null when it does not. The
 * module rule covers everything in the module, now and later; a pick is one
 * surface.
 */
export function capabilityGrantOf(
  capability: LicenseFeatureGroupCapability,
  rules: readonly FeatureGroupRuleLike[],
  isPicked: (code: string) => boolean,
): LicenseFeatureGroupCapabilityGrant | null {
  if (hasModuleRule(rules, capability.ModuleCode)) return "module"
  if (isPicked(capability.Code)) return "pick"
  return null
}

/** The rule set with one module's "everything, now and later" set or cleared. */
export function rulesWithModuleRule(
  rules: readonly FeatureGroupRuleLike[],
  moduleCode: string,
  granted: boolean,
): FeatureGroupRuleLike[] {
  const kept = rules.filter((rule) => !isModuleRule(rule, moduleCode))
  if (!granted) return kept
  return [
    ...kept,
    {
      RuleKindCode: FEATURE_GROUP_RULE_KIND.module,
      FeatureTypeCode: null,
      LayerCategoryFeatureCode: null,
      ModuleCode: moduleCode,
    },
  ]
}

export function isModuleRoot(capability: LicenseFeatureGroupCapability) {
  return isModuleRootKind(capability.KindCode)
}

/** Genuinely gated: a package that lacks it locks it. "Available for All" never locks. */
export function isLockable(capability: LicenseFeatureGroupCapability) {
  return (
    capability.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  )
}

/**
 * What one tick changes. Granting a row grants what it needs to be reached
 * (its tab, its panel); revoking a row revokes what sits inside it, which
 * the console could not reach anyway. The tree, not the operator, keeps
 * the package consistent.
 */
export function pickClosureOf(
  tree: LicenseFeatureGroupCapabilityTree,
  capability: LicenseFeatureGroupCapability,
  picked: boolean,
) {
  return picked
    ? [capability, ...tree.ancestorsOf(capability.Code)]
    : [capability, ...tree.descendantsOf(capability.Code)]
}

/** The name as the editor shows it: read off the code, for a row whose text is not authored. */
export function nameOf(capability: LicenseFeatureGroupCapability) {
  return isPlaceholderName(capability)
    ? humanizeFeatureCode(capability.Code)
    : capability.Name
}

/** A module's name as the editor shows it: read off the code when its root text is not authored. */
export function moduleNameOf(capability: LicenseFeatureGroupCapability) {
  return capability.ModuleName === capability.ModuleCode
    ? humanizeFeatureCode(capability.ModuleCode)
    : capability.ModuleName
}

/**
 * The kind word a row carries. A module root that is the only thing in its
 * module IS the whole module; one with parts beneath it is the module's own
 * entry, and the parts are granted separately.
 */
export function kindLabelOf(
  capability: LicenseFeatureGroupCapability,
  hasParts: boolean,
) {
  if (!isModuleRoot(capability)) return capability.KindName
  return hasParts ? "Module" : "Whole module"
}

function byRootThenName(
  a: LicenseFeatureGroupCapability,
  b: LicenseFeatureGroupCapability,
) {
  if (isModuleRoot(a) !== isModuleRoot(b)) return isModuleRoot(a) ? -1 : 1
  return a.Name.localeCompare(b.Name) || a.Code.localeCompare(b.Code)
}

/**
 * One module's capabilities as a tree: the module root (when the module
 * has one) with everything else beneath it — tabs under the panel root,
 * sub-tabs and panel modules under their tab, the map tools under the Map
 * root — each level by name. A parent the catalog does not list in this
 * module makes its children top-level.
 */
export type LicenseFeatureGroupCapabilityTree = {
  /** The module's own surface, when it is gated as a whole. */
  root: LicenseFeatureGroupCapability | undefined
  /** Rows with no parent in the module — the root alone when there is one. */
  topLevel: readonly LicenseFeatureGroupCapability[]
  childrenOf(code: string): readonly LicenseFeatureGroupCapability[]
  parentOf(code: string): LicenseFeatureGroupCapability | undefined
  /** Parent, grandparent, … — what a row needs to be reachable. */
  ancestorsOf(code: string): LicenseFeatureGroupCapability[]
  /** Everything beneath a row — what becomes unreachable without it. */
  descendantsOf(code: string): LicenseFeatureGroupCapability[]
}

export function capabilityTreeOf(
  capabilities: readonly LicenseFeatureGroupCapability[],
): LicenseFeatureGroupCapabilityTree {
  const byCode = new Map(
    capabilities.map((capability) => [capability.Code, capability]),
  )
  const children = new Map<string, LicenseFeatureGroupCapability[]>()
  const topLevel: LicenseFeatureGroupCapability[] = []
  for (const capability of capabilities) {
    const parent = capability.ParentCode
    if (parent && byCode.has(parent)) {
      const siblings = children.get(parent)
      if (siblings) siblings.push(capability)
      else children.set(parent, [capability])
    } else {
      topLevel.push(capability)
    }
  }
  topLevel.sort(byRootThenName)
  for (const siblings of children.values()) siblings.sort(byRootThenName)

  const childrenOf = (code: string) => children.get(code) ?? []
  const parentOf = (code: string) => {
    const parent = byCode.get(code)?.ParentCode
    return parent ? byCode.get(parent) : undefined
  }
  const ancestorsOf = (code: string) => {
    const ancestors: LicenseFeatureGroupCapability[] = []
    for (let parent = parentOf(code); parent; parent = parentOf(parent.Code)) {
      ancestors.push(parent)
    }
    return ancestors
  }
  const descendantsOf = (code: string): LicenseFeatureGroupCapability[] =>
    childrenOf(code).flatMap((child) => [child, ...descendantsOf(child.Code)])

  return {
    root: topLevel.find(isModuleRoot),
    topLevel,
    childrenOf,
    parentOf,
    ancestorsOf,
    descendantsOf,
  }
}

/**
 * How many parts the hover card names before it counts the rest. A tab with
 * two sub-tabs is named in full; a module with fifteen tabs is "ESG,
 * Government cases, Listing, Overview, Owners and 10 more" — the rows sit
 * right beneath the header, and a card that lists them all is a card nobody
 * reads to the end.
 */
const NAMED_PARTS_LIMIT = 5

/** "A, B, C and 10 more" past the limit; the plain list up to it. */
function nameSome(names: readonly string[], limit = NAMED_PARTS_LIMIT) {
  if (names.length <= limit) return names.join(", ")
  return `${names.slice(0, limit).join(", ")} and ${names.length - limit} more`
}

/** What granting the row includes, as the hover card says it; null when nothing to say. */
function containsOf(
  capability: LicenseFeatureGroupCapability,
  parts: readonly LicenseFeatureGroupCapability[],
) {
  if (parts.length > 0) {
    return `${nameSome(parts.map(nameOf))} — capabilities of their own, granted separately`
  }
  if (isModuleRoot(capability)) {
    return `nothing in ${capability.ModuleName} is granted separately`
  }
  return null
}

export type LicenseFeatureGroupCapabilityFact = { label: string; value: string }

/**
 * The detail a row keeps off the page — what it is, what it sits in, what
 * granting it includes, how it is gated, who carries it — for the hover
 * card. One builder, so a module that is a single line and a row deep in a
 * tree explain themselves the same way.
 */
export function capabilityFactsOf(
  capability: LicenseFeatureGroupCapability,
  tree: LicenseFeatureGroupCapabilityTree,
): LicenseFeatureGroupCapabilityFact[] {
  const parent = tree.parentOf(capability.Code)
  const parts = tree.childrenOf(capability.Code)
  const contains = containsOf(capability, parts)
  return [
    { label: "Kind", value: kindLabelOf(capability, parts.length > 0) },
    ...(parent ? [{ label: "Part of", value: nameOf(parent) }] : []),
    ...(contains ? [{ label: "Alongside", value: contains }] : []),
    { label: "Module", value: moduleNameOf(capability) },
    { label: "Checked by", value: describeCheckedBy({ kind: "console" }) },
    { label: "Who gets it", value: describeWhoGetsIt(isLockable(capability)) },
    describePackagesFact(isLockable(capability), packagesOf(capability)),
    ...(isPlaceholderName(capability)
      ? [
          {
            label: "Name",
            value: "read off the code; its text is not written yet",
          },
        ]
      : []),
  ]
}

/** "17 tabs, 2 sub-tabs, 1 panel module" — what a module is made of. */
export function describeKinds(
  capabilities: readonly LicenseFeatureGroupCapability[],
) {
  const counts = new Map<string, number>()
  for (const capability of capabilities) {
    if (isModuleRoot(capability)) continue
    const kind = capability.KindName.toLowerCase()
    counts.set(kind, (counts.get(kind) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([kind, count]) => pluralize(count, kind))
    .join(", ")
}

export function packagesOf(capability: LicenseFeatureGroupCapability) {
  return (capability.FeatureGroupNames ?? "").split(", ").filter(Boolean)
}

/** "Basic, Standard" minus the package being edited, or null. */
export function otherPackagesOf(
  capability: LicenseFeatureGroupCapability,
  featureGroupName: string,
) {
  const names = packagesOf(capability).filter(
    (name) => name !== featureGroupName,
  )
  return names.length > 0 ? names.join(", ") : null
}

/** How much of the module each other package carries today, biggest first. */
export function coverageOf(
  capabilities: readonly LicenseFeatureGroupCapability[],
  featureGroupName: string,
) {
  const counts = new Map<string, number>()
  for (const capability of capabilities) {
    for (const name of packagesOf(capability)) {
      if (name === featureGroupName) continue
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])
}
